import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Download, Phone, Mail } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PRODUCT_BY_SLUG, CATEGORY_BY_SLUG, PRODUCTS, SHEET_URL, SITE } from "../data/products";

const ProductDetail = () => {
  const { slug } = useParams();
  const product = PRODUCT_BY_SLUG[slug];

  if (!product) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 px-6 pt-24 text-center">
        <h1 className="font-display text-3xl font-extrabold text-night">Product not found</h1>
        <Link to="/products" data-testid="product-notfound-back" className="rounded-full bg-night px-6 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white">
          Back to products
        </Link>
      </div>
    );
  }

  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 3);

  const specs = [
    ["Category", CATEGORY_BY_SLUG[product.category]],
    ["Physical form", product.form],
    ["Recommended species", product.species.join(", ")],
    ["Application / use", product.use],
    ["Inclusion rate", product.inclusion],
    ["Availability", product.status === "coming-soon" ? "Coming soon — on request" : "Available — samples on request"],
  ];

  return (
    <div data-testid={`page-product-${product.slug}`}>
      <Seo
        title={product.name}
        description={`${product.name} — ${product.tagline}. ${product.form}; inclusion ${product.inclusion}. From AURIK INGREVIA (P) Ltd.`}
      />

      <section className="grain mesh-glow relative overflow-hidden pb-16 pt-36 lg:pt-44">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Link
            to="/products"
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-200/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All products
          </Link>

          <div className="mt-10 grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="clip-frame relative overflow-hidden"
              data-testid="product-detail-image"
            >
              <img src={product.image} alt={`${product.name} — ${product.tagline}`} className="h-72 w-full object-cover sm:h-96 lg:h-[440px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-night/60 via-transparent to-transparent" />
            </motion.div>

            <div>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-teal-300">
                {CATEGORY_BY_SLUG[product.category]}
              </span>
              <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                {product.name}
              </h1>
              <p className="mt-3 text-lg font-medium text-emerald-100/80">{product.tagline}</p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to={`/contact?product=${encodeURIComponent(product.name)}`}
                  data-testid="product-request-sample-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-night transition-colors hover:bg-amber-400"
                >
                  Request sample / quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href={SHEET_URL(product.slug)}
                  data-testid="product-download-sheet-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:border-teal-400 hover:text-teal-300"
                >
                  <Download className="h-4 w-4" /> Download product sheet
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-6 font-mono text-[11px] uppercase tracking-[0.16em] text-emerald-200/70">
                <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="product-phone-link" className="inline-flex items-center gap-2 hover:text-white">
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
                <a href={`mailto:${SITE.email}`} data-testid="product-email-link" className="inline-flex items-center gap-2 hover:text-white">
                  <Mail className="h-4 w-4" /> {SITE.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-night sm:text-3xl">Product profile</h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">{product.description}</p>

            <h3 className="mt-10 font-display text-lg font-bold text-night">Key features</h3>
            <ul className="mt-4 space-y-3">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 rounded-full bg-emerald-100 p-1 text-emerald-800">
                    <Check className="h-3 w-3" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-night sm:text-3xl">Specifications</h2>
            <dl className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="product-specs-table">
              {specs.map(([k, v], i) => (
                <div key={k} className={`spec-row flex flex-col gap-1 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8 ${i !== 0 ? "border-t border-slate-100" : ""}`}>
                  <dt className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{k}</dt>
                  <dd className="text-sm font-medium text-slate-800 sm:text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Inclusion rates are guidance for finished feed; confirm final dosage with our
              technical team for your base feed and process conditions.
            </p>
          </Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-slate-200 bg-white py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-night">More in {CATEGORY_BY_SLUG[product.category]}</h2>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Reveal key={r.slug} className="h-full">
                  <Link
                    to={`/products/${r.slug}`}
                    data-testid={`related-product-${r.slug}`}
                    className="group flex h-full items-center gap-5 rounded-2xl border border-slate-200 p-5 transition-colors hover:border-teal-600/50"
                  >
                    <img src={r.image} alt={r.name} loading="lazy" className="h-20 w-20 shrink-0 rounded-xl object-cover" />
                    <div>
                      <h3 className="font-display text-lg font-bold text-night">{r.name}</h3>
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{r.tagline}</p>
                    </div>
                    <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-teal-600" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
